// /series splash — the AI-podcast-generator landing page: hero, how-it-works, the six-card fold, the live featured directory, eight anchored feature sections, the cross-page seams, and one closing CTA.
// Loader-only ⇒ pure SSG: fetched once at build, baked into the static HTML. No clientLoader/HydrateFallback (those bake a spinner + re-fetch live on hydrate, defeating SSG/SEO).
// Node-prerender-safe: fetch + MUI + local helpers only — no store/Amplify/@shared imports (this module's whole graph is eagerly imported during Node prerender, wave-1's gotcha). `src/shared/config/credits` is import-free consts, so it is safe here and is the only place a dollar figure may come from.
// Chrome (theme + nav + footer) comes from splash-chrome, which every marketing page shares; it provides the ThemeProvider, so this file must NOT also wrap `Mui`.
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import CheckIcon from '@mui/icons-material/Check'; // Control-family affordance, exempt from the one-outlined-family rule (vocabulary *Icons*) — not a domain glyph.
// Icon choice is a COPY decision, not just a visual one: MUI bakes the component name into a `data-testid` that survives the production build, so `AutoStories` would leak the literal string "Stories" into the prerendered HTML — an A13 violation ("Series", never "Stories") that no typecheck or source review can see.
import HubIcon from '@mui/icons-material/HubOutlined';
import { SplashChrome, AUTH_SIGN_UP } from './splash-chrome';
import { Anchor, Band, SectionHeader, FeatureCard, CtaBand, type FeatureTone } from './splash-sections';
import { MigrationNotice } from './series-migration';
import { FeatureCards, FeatureSections, FEATURE_LIST } from './series-features';
import { fetchFeaturedShows, type FeaturedShow } from './series-data';
import { metaDescription, splashMeta, softwareApplicationLd, ORIGIN } from './series-ld';
import { CREDIT_TOPUP_DISPLAY } from '../../../../shared/config/credits';
import type { Route } from './+types/series';

const TITLE = 'AI Podcast Generator — Gnothi Series';
const DESCRIPTION = metaDescription(
  'Gnothi turns your journal - or a world you invent - into a narrated podcast: deep research per segment, autopilot episodes, and a real RSS feed.',
);
const PATH = '/series';

export async function loader(): Promise<{ featured: FeaturedShow[] }> {
  // Fail-soft is deliberate (atom #5) but must not be silent: this runs at PRERENDER, so a transient artifact fetch failure bakes an empty splash into the deploy and the build still goes green. The build log is the only place anyone could ever see it.
  try { return { featured: await fetchFeaturedShows() }; }
  catch (e) { console.warn('[series] featured fetch failed — rendering an empty directory', e); return { featured: [] }; }
}

export const meta: Route.MetaFunction = () => [
  ...splashMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
  {
    // The LD `url` deep-links the generator section while og:url/canonical stay bare `/series` — that asymmetry is deliberate, and it is why `splashMeta` does not own this descriptor.
    'script:ld+json': softwareApplicationLd({
      name: 'Gnothi Series',
      description: DESCRIPTION,
      url: `${ORIGIN}${PATH}#podcast-generator`,
      applicationCategory: 'MultimediaApplication',
      featureList: FEATURE_LIST,
    }),
  },
];

const STEPS = [
  'Describe the Series you want - or point it at the journal you already keep.',
  'Gnothi researches, writes, and narrates each chapter on the cadence you set.',
  'Read it, listen to it, or subscribe in any podcast app.',
];

const SEAMS: { to: string; icon: ReactNode; tone: FeatureTone; title: string; body: string }[] = [
  { to: '/fields', icon: <InsightsIcon />, tone: 'behavior', title: 'Fields feed your chapters', body: 'What you track - sleep, habits, pages written - and the causal analysis over it land in the same chapter context your entries do.' },
  { to: '/community', icon: <GroupsIcon />, tone: 'themes', title: 'Shared entries weave in', body: 'Entries a group or a person shares with you can become source material for your Series, on the same scope rules as your own.' },
  { to: '/', icon: <HubIcon />, tone: 'prompt', title: 'See the whole picture', body: 'Journal, fields, community, and Series are one product: you write, and Gnothi authors the book of your life a chapter at a time.' },
];

// One claim, one proof, one action. The proof strip RESTATES three claims the page evidences in full further down (`tts`'s lead, `publish`'s and `podcast-generator`'s points) — it does not reword them, so their citations stay put in series-features.tsx.
const PROOF = ['Narration is free on every account', 'A real RSS feed - Apple Podcasts, Spotify, Overcast', 'Chapters up to ~2 hours, not 3-minute clips'];

function Hero() {
  return (
    <Band color="dark" hero>
      <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center' }}>
        {/* `fontSize` ramp because the theme's h2 is FLAT (no breakpoints), so 3rem would land at 48px on a 360px phone. */}
        <Typography variant="h2" component="h1" sx={{ fontWeight: 800, maxWidth: 900, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
          An AI podcast generator that writes from your life.
        </Typography>
        <Typography variant="h6" component="p" sx={{ fontWeight: 400, opacity: 0.85, maxWidth: 720 }}>
          Your journal - or a world you invent - becomes chapters: written, narrated, and published to a real RSS feed on the cadence you set.
        </Typography>
        <Stack direction="row" spacing={{ xs: 1.5, sm: 3 }} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center', color: 'grey.300' }}>
          {PROOF.map((t) => (
            <Stack key={t} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}><CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} /><Typography variant="body2">{t}</Typography></Stack>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
          <Button component={RouterLink} to={AUTH_SIGN_UP} variant="contained" size="large" sx={{ px: 4 }}>
            Get started free
          </Button>
          <Button component="a" href="#featured" variant="outlined" color="inherit" size="large">
            Hear what people made
          </Button>
        </Stack>
      </Stack>
    </Band>
  );
}

function HowItWorks() {
  return (
    <Anchor sx={{ mb: { xs: 6, md: 8 } }}>
      <SectionHeader title="How it works" />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 5 }} sx={{ justifyContent: 'center', alignItems: 'flex-start' }}>
        {STEPS.map((text, i) => (
          <Stack key={i} spacing={1.5} sx={{ maxWidth: 300, alignItems: 'center' }}>
            {/* The accent FILL pair, not `primary.main`/`primary.contrastText`: white on `#00A8E8` is 2.7:1, under even the 3:1 large-text threshold this 1.25rem/700 numeral qualifies for. */}
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'splash.accent', color: 'splash.onAccent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.25rem' }}>
              {i + 1}
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>{text}</Typography>
          </Stack>
        ))}
      </Stack>
    </Anchor>
  );
}

function ShowCard({ row }: { row: FeaturedShow }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardActionArea component={RouterLink} to={`/series/${row.slug}`} sx={{ height: '100%', p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          {/* `loading="lazy"` is not a nicety here: these are the raw podcast covers (Apple's 1400px floor — one measured 3.26 MB) painted into a 64px slot, and `#featured` sits several folds down, so without it the prerendered HTML kicks off up to `limit` multi-MB fetches at parse time against the hero's own paint. No CLS risk to trade away — the box is fixed 64×64 below. */}
          <Box component="img" src={row.cover_url} alt={row.title} loading="lazy"
            sx={{ width: 64, height: 64, borderRadius: 1, objectFit: 'cover', flexShrink: 0, bgcolor: 'action.hover' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600 }} noWrap>{row.title}</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{row.owner_name}</Typography>
            <Typography variant="caption" color="text.secondary">{row.episode_count} episode{row.episode_count === 1 ? '' : 's'}</Typography>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

// The whole featured list is baked into the loader data, but only the first row of cards paints until asked: `expanded` starts false on BOTH prerender and hydrate, so the folded HTML matches — no mismatch — and the rest reveal client-side with zero refetch. Covers stay `loading="lazy"`, so an expand kicks off no fetch storm.
const FEATURED_FOLD = 12;

// Fail-soft (goal atom #5): an unpublished or unreachable directory artifact must never break the page. The SECTION and its `#featured` id render unconditionally, because the hero's secondary CTA and §3 both scroll-target it and a missing anchor is its own defect — only the grid is conditional.
function FeaturedShows({ rows }: { rows: FeaturedShow[] }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, FEATURED_FOLD);
  return (
    <Anchor id="featured">
      <SectionHeader title="Featured Series" deck="Real journals and invented worlds, turned into podcasts by Gnothi users." />
      {rows.length ? (
        <>
          <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
            {visible.map((row) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={row.id}>
                <ShowCard row={row} />
              </Grid>
            ))}
          </Grid>
          {rows.length > FEATURED_FOLD && (
            <Stack sx={{ alignItems: 'center', mt: 4 }}>
              <Button variant="outlined" onClick={() => setExpanded((e) => !e)}>
                {expanded ? 'Show fewer' : `Show all ${rows.length} series`}
              </Button>
            </Stack>
          )}
        </>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>The directory is refreshing - check back in a moment.</Typography>
      )}
    </Anchor>
  );
}

function Seams() {
  return (
    <Anchor id="connects">
      <SectionHeader title="Where Series connects" deck="A Series is not a silo - it reads from everything else you keep in Gnothi." />
      <Grid container spacing={2}>
        {SEAMS.map(({ to, icon, tone, title, body }) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={to}>
            <FeatureCard title={title} icon={icon} tone={tone} to={to}>{body}</FeatureCard>
          </Grid>
        ))}
      </Grid>
    </Anchor>
  );
}

export default function SeriesIndex({ loaderData }: Route.ComponentProps) {
  return (
    <SplashChrome active="series">
      <Hero />
      {/* Below the hero, not above it: `MigrationNotice` decides its state in a useEffect, so it renders NOTHING at prerender and then mounts — above the H1 that shifted the flagship hero down on every single visit. `Container` is legal here only because this strip sits outside every `Band` (`Section` owns the padding inside one). A `?migrated=podcaster` arrival is still one flick from the open Alert. */}
      <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 } }}><MigrationNotice /></Container>
      <Band color="grey">
        <HowItWorks />
        <Anchor id="features">
          <SectionHeader title="Everything a Series does" deck="Tap any capability to jump straight to its detail - and keep scrolling past the grid for your ad rolls and the reader/player." />
          <FeatureCards />
        </Anchor>
      </Band>
      <Band color="light"><FeaturedShows rows={loaderData.featured} /></Band>
      <Band color="grey"><FeatureSections group="The production loop" /></Band>
      <Band color="light"><FeatureSections group="What it writes from" /></Band>
      <Band color="grey"><FeatureSections group="Where it goes" /></Band>
      {/* The page's ONE accent band (hero + at most two are allowed, and it spent only the hero): the "it is all one product" statement earns the crescendo before the close. Dark-safe, checked in the emitted CSS: `Seams` carries no `text.primary` span, and `FeatureCard`'s Paper is an opaque `background.paper` even on the navy wash, so its own `text.primary` body stays on paper. */}
      <Band color="dark"><Seams /></Band>
      <CtaBand color="light"
        title="Start your first Series."
        deck={`Narration, the four preset voices, and your RSS feed are free on every account. Credits are a one-time ${CREDIT_TOPUP_DISPLAY} top-up for cloning and generated cover art - never a subscription.`}
      />
    </SplashChrome>
  );
}
